import { EPaymentPlan } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { CircularProgress, Stack, Typography } from '@mui/material'
import {
  FormatPaymentPlanForSummary,
  getMerchantFees,
  getSelectedPlan,
  getTotalObligation,
} from '@src/data/worksheet-selectors'
import { ComputeFundingResultDto } from '@src/types/ComputePersonalLoanFundingResultDto'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { TermAndPlanOption } from '@src/types/TermPlanOptionDto'
import { useTranslation } from 'react-i18next'

interface RowProbs {
  label: string
  value: string
}

const RowStack = ({ label, value }: RowProbs) => {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography>{label} :</Typography>
      <Typography>{value}</Typography>
    </Stack>
  )
}

interface Props {
  creditApplication: CreditApplication
  amountRequested: number
  hasVariableInterest: boolean
  firstPaymentOn: string | null
  merchant: Merchant
  paymentPlanId: EPaymentPlan
  computedPersonalLoanFunding: ComputeFundingResultDto | null
  isComputing: boolean
  selectedTermAndPlanOption?: TermAndPlanOption
}
const PersonalLoanFundingSummary = ({
  creditApplication,
  amountRequested,
  hasVariableInterest,
  firstPaymentOn,
  merchant,
  paymentPlanId,
  computedPersonalLoanFunding,
  isComputing,
  selectedTermAndPlanOption,
}: Props) => {
  const { t } = useTranslation()
  const totalObligation = getTotalObligation(amountRequested, computedPersonalLoanFunding)
  const selectedPlan = getSelectedPlan(merchant, paymentPlanId)
  const insuranceTotal =
    (computedPersonalLoanFunding?.insuranceFee ?? 0) + (computedPersonalLoanFunding?.insuranceTax ?? 0)

  const interestRateLabel = selectedTermAndPlanOption
    ? selectedTermAndPlanOption.promoDurationMonths
      ? `${selectedTermAndPlanOption.promoRate}%/${selectedTermAndPlanOption.promoDurationMonths} → ${selectedTermAndPlanOption.regularRate}%`
      : `${computedPersonalLoanFunding?.interestRate ?? selectedTermAndPlanOption.promoRate}%`
    : hasVariableInterest
      ? FormatPaymentPlanForSummary(selectedPlan)
      : `${creditApplication.finalCreditDecision.interestRate}%`
  const merchantFeeRateForDisplay =
    computedPersonalLoanFunding?.merchantFeeRate ?? selectedTermAndPlanOption?.merchantFeeRate ?? selectedPlan?.merchantFeeRate

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="subtitle1" fontWeight="bold">
        {t('worksheet.fundingSummary')} :
      </Typography>
      {isComputing && (
        <div style={{ textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )}
      {!computedPersonalLoanFunding && !isComputing && <Typography>{t('worksheet.personalFundingInvalid')}</Typography>}
      {computedPersonalLoanFunding && (
        <>
          <RowStack label={t('worksheet.requestedAmount')} value={formatCurrency(amountRequested)} />
          <RowStack
            label={`${t('worksheet.interest')} (${interestRateLabel})`}
            value={formatCurrency(computedPersonalLoanFunding.totalInterestAmount)}
          />

          <RowStack
            label={`${t('worksheet.financeFee')} (${computedPersonalLoanFunding.lenderFeeRate}%)`}
            value={formatCurrency(computedPersonalLoanFunding.lenderFee)}
          />

          {hasVariableInterest && (
            <RowStack
              label={`${t('worksheet.merchantFee')} (${merchantFeeRateForDisplay}%)`}
              value={getMerchantFees(merchantFeeRateForDisplay, amountRequested)}
            />
          )}

          <RowStack label={t('worksheet.protectionPlan')} value={formatCurrency(insuranceTotal)} />

          <RowStack label={t('worksheet.totalObligation')} value={formatCurrency(totalObligation)} />

          <RowStack
            label={t('worksheet.annualPercentageRate')}
            value={`${computedPersonalLoanFunding?.effectiveRate}%`}
          />

          <Stack direction="row" justifyContent="space-between" paddingTop={3}>
            <Typography variant="subtitle1" fontWeight="bold">
              {t('worksheet.paymentTitle')}
            </Typography>
          </Stack>

          <RowStack
            label={t('worksheet.maxPmtAmount')}
            value={formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}
          />

          <RowStack label={t('worksheet.paymentFrequency')} value={t('common.monthly')} />

          <RowStack
            label={t('worksheet.monthlyPayment')}
            value={formatCurrency(computedPersonalLoanFunding?.paymentForFrequency)}
          />

          <RowStack label={t('worksheet.firstPaymentOn')} value={firstPaymentOn ? formatDate(firstPaymentOn) : ''} />

          <Stack direction="row" justifyContent="space-between" paddingTop={3}>
            <Typography fontWeight="bold">{t('worksheet.amountToBePaid')} :</Typography>
            <Typography>{formatCurrency(amountRequested ?? 0)}</Typography>
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default PersonalLoanFundingSummary
